import React, { useState } from "react";
import { Server, Circle, RefreshCw, ExternalLink } from "lucide-react";

const ServerPanel = ({
  connected,
  onConnect,
  onDisconnect,
  onMessage,
  endpoint,
}) => {
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await onConnect();
      onMessage("Connected to PlusE Finance MCP server", "success");
    } catch (error) {
      onMessage(`Failed to connect to MCP server: ${error.message}`, "error");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await onDisconnect();
      onMessage("Disconnected from MCP server", "info");
    } catch (error) {
      onMessage(`Failed to disconnect: ${error.message}`, "error");
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">MCP Server</h2>
            <p className="text-sm text-gray-500 mt-1">
              PlusE Finance Model Context Protocol Server
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!connected ? (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {connecting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Server className="w-4 h-4" />
                )}
                {connecting ? "Connecting..." : "Connect"}
              </button>
            ) : (
              <button
                onClick={handleDisconnect}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <Circle className="w-4 h-4" />
                Disconnect
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Server Status */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-800 mb-3">
            Server Status
          </h3>
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Circle
                    className={`w-3 h-3 ${
                      connected
                        ? "fill-green-400 text-green-400"
                        : "fill-red-400 text-red-400"
                    }`}
                  />
                  <span className="font-medium text-gray-800">
                    PlusE Finance MCP
                  </span>
                </div>
              </div>

              <span
                className={`text-sm px-2 py-1 rounded ${
                  connected
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {connected ? "Connected" : "Disconnected"}
              </span>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <span>Endpoint:</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {endpoint}
                  </code>
                  <a
                    href={endpoint}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span>Protocol:</span>
                <span>Model Context Protocol (MCP)</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Type:</span>
                <span>HTTP/WebSocket</span>
              </div>
            </div>
          </div>
        </div>

        {/* Server Information */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-800 mb-3">
            About This Server
          </h3>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-blue-900">
                  PlusE Finance MCP Server
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  This is a hardcoded connection to the PlusE Finance Model
                  Context Protocol server. It provides financial tools and
                  resources for AI-powered financial analysis.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-900">Features:</span>
                  <ul className="text-blue-700 mt-1 space-y-1">
                    <li>• Financial data tools</li>
                    <li>• Market analysis</li>
                    <li>• Real-time data access</li>
                  </ul>
                </div>
                <div>
                  <span className="font-medium text-blue-900">
                    Capabilities:
                  </span>
                  <ul className="text-blue-700 mt-1 space-y-1">
                    <li>• Tool execution</li>
                    <li>• Resource access</li>
                    <li>• WebSocket support</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Instructions */}
        {!connected && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              Getting Started
            </h3>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  Click the "Connect" button above to establish a connection to
                  the PlusE Finance MCP server.
                </p>
                <p>Once connected, you'll be able to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Browse available tools in the Tools panel</li>
                  <li>Execute financial analysis tools</li>
                  <li>Access financial data resources</li>
                  <li>Use the chat interface for interactive queries</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerPanel;
